// Linear dimension in the lock calculator's style (port of its LinearDimension): extension lines from the measured
// points past the dimension line, the dimension line, an arrowhead at each end pointing outwards and the label beside
// the middle, on the outer side. The command comes from the shared scene builder (metres): its offset is 0, the line
// being already placed off the elements, and offsetDir is the outer side (none: see fallbackOuterSide).
import { Line } from "@react-three/drei";
import { memo, useMemo, type FC } from "react";
import * as THREE from "three";
import type { DimLineCommand } from "../tc3dViewer";
import Arrowhead from "./Arrowhead";
import QuoteLabel from "./QuoteLabel";
import { QUOTE, QUOTE_COLORS } from "./viewerConfig";

export type LinearDimensionProps = {
  command: DimLineCommand;
};

const LinearDimension: FC<LinearDimensionProps> = ({ command }) => {
  const geometry = useMemo(() => buildLinearGeometry(command), [command]);
  if (!geometry) return null;

  return (
    <group>
      <Line points={geometry.extensionA} color={QUOTE_COLORS.extensionLine} />
      <Line points={geometry.extensionB} color={QUOTE_COLORS.extensionLine} />
      <Line points={geometry.dimensionLine} color={QUOTE_COLORS.dimensionLine} />
      <Arrowhead
        tip={geometry.dimensionLine[0]}
        direction={geometry.backward}
        color={QUOTE_COLORS.arrow}
        length={geometry.arrowLength}
        width={geometry.arrowWidth}
      />
      <Arrowhead
        tip={geometry.dimensionLine[1]}
        direction={geometry.forward}
        color={QUOTE_COLORS.arrow}
        length={geometry.arrowLength}
        width={geometry.arrowWidth}
      />
      <QuoteLabel position={geometry.labelPosition} text={command.label} awayFrom={geometry.outward} />
    </group>
  );
};

type LinearGeometry = {
  extensionA: [THREE.Vector3, THREE.Vector3];
  extensionB: [THREE.Vector3, THREE.Vector3];
  dimensionLine: [THREE.Vector3, THREE.Vector3];
  // unit vectors along the line: the arrows point outwards (tips on the extension lines)
  forward: THREE.Vector3;
  backward: THREE.Vector3;
  arrowLength: number;
  arrowWidth: number;
  labelPosition: THREE.Vector3;
  outward: THREE.Vector3;
};

// null for a degenerate segment: nothing to measure, the arrows would overlap on a point.
function buildLinearGeometry(command: DimLineCommand): LinearGeometry | null {
  const { p1: from, p2: to, offsetAmt: offset } = command;
  if (from.distanceToSquared(to) < 1e-12) return null;
  const forward = to.clone().sub(from).normalize();
  const outward =
    command.offsetDir.lengthSq() > 1e-12 ? command.offsetDir.clone().normalize() : fallbackOuterSide(forward);

  const dimFrom = from.clone().addScaledVector(outward, offset);
  const dimTo = to.clone().addScaledVector(outward, offset);
  const length = dimFrom.distanceTo(dimTo);
  const arrowLength = Math.min(QUOTE.arrowLength, length * QUOTE.arrowMaxFraction);

  return {
    extensionA: [from, from.clone().addScaledVector(outward, offset + QUOTE.extensionOvershoot)],
    extensionB: [to, to.clone().addScaledVector(outward, offset + QUOTE.extensionOvershoot)],
    dimensionLine: [dimFrom, dimTo],
    forward,
    backward: forward.clone().negate(),
    arrowLength,
    arrowWidth: (QUOTE.arrowWidth * arrowLength) / QUOTE.arrowLength,
    labelPosition: dimFrom.clone().add(dimTo).multiplyScalar(0.5).addScaledVector(outward, QUOTE.labelGap),
    outward,
  };
}

// A dimension with no outer side (offsetDir zero): perpendicular to the line in the connection plane, upwards (to the
// right for a vertical line); up for a line along z.
function fallbackOuterSide(forward: THREE.Vector3): THREE.Vector3 {
  const side = new THREE.Vector3(-forward.y, forward.x, 0);
  if (side.lengthSq() < 1e-12) return new THREE.Vector3(0, 1, 0);
  side.normalize();
  if (side.y < -1e-9 || (Math.abs(side.y) <= 1e-9 && side.x < 0)) side.negate();
  return side;
}

export default memo(LinearDimension);
